import request from './request'
import {
  getMockCouponListResponse,
  shouldUseCouponMock,
} from './couponCenterMock'
import {
  resolveEligibleServicesText,
  resolveValidityPeriodText,
  type CouponDetailData,
} from './CouponDetai'

export interface ApiResponse<T = unknown> {
  code: number
  data: T
  msg: string
}

export interface Pagination {
  page: number
  page_size: number
  total: number
  total_pages: number
}

export interface CouponCenterListItem {
  id: number
  coupon_id: string
  name: string
  code: string
  expiry_text?: string
  usage_text?: string
  discount_text?: string
  min_spend_text?: string
  eligible_text?: string
  visibility?: string
  visibility_text?: string
  availability?: string
  availability_text?: string
  type?: string
  [key: string]: unknown
}

export interface CouponCenterListData {
  list: CouponCenterListItem[]
  pagination: Pagination
}

export interface CouponCenterListParams {
  page: number
  page_size: number
  search_type?: string
  keyword?: string
  type?: string
  eligible_service?: string
  visibility?: string
  availability?: string
}

export interface CouponItemView {
  id: string
  recordId: number
  name: string
  code: string
  validUntil: string
  usage: string
  valueType: string
  value: string
  spendRequirement: string
  eligibility: string
  visibility: string
  status: string
  visibilityRaw: string
  availabilityRaw: string
}

const SEARCH_TYPE_ALIAS: Record<string, string> = {
  code: 'coupon_code',
  name: 'coupon_name',
  coupon_code: 'coupon_code',
  coupon_name: 'coupon_name',
}

function resolveSearchType(searchField: string): string {
  const key = searchField.trim()
  return SEARCH_TYPE_ALIAS[key] ?? key
}

function splitDiscountText(text?: string): { valueType: string; value: string } {
  const raw = (text ?? '').trim()
  if (!raw) return { valueType: '', value: '' }

  const moneyMatch = raw.match(/^(.*?)\s+(\$\d[\d.,]*)$/)
  if (moneyMatch?.[1] && moneyMatch[2]) {
    return { valueType: moneyMatch[1].trim(), value: moneyMatch[2].trim() }
  }

  const percentMatch = raw.match(/^(.*?)\s+(\d+(?:\.\d+)?%)$/)
  if (percentMatch?.[1] && percentMatch[2]) {
    return { valueType: percentMatch[1].trim(), value: percentMatch[2].trim() }
  }

  return { valueType: raw, value: '' }
}

/** meta 可能给 `1`/`0`，列表/更新接口用 `active`/`inactive` */
function resolveAvailabilityFilter(value: string): string {
  if (value === 'all') return 'all'
  const normalized = value.trim().toLowerCase()
  if (normalized === '1' || normalized === 'active') return 'active'
  if (normalized === '0' || normalized === 'inactive') return 'inactive'
  return normalized
}

/** meta 可能给 `1`/`0`，列表/更新接口用 `visible`/`hidden` */
function resolveVisibilityFilter(value: string): string {
  if (value === 'all') return 'all'
  const normalized = value.trim().toLowerCase()
  if (normalized === '1' || normalized === 'visible') return 'visible'
  if (normalized === '0' || normalized === 'hidden') return 'hidden'
  return normalized
}

/**
 * 未选：不传（避免与 All Services 的 `all` 冲突）。
 * All services：入库/列表筛选用 `all`（表单值为 `all_services`）。
 */
function resolveEligibleServiceFilter(value: string): string | undefined {
  const normalized = value.trim().toLowerCase()
  if (!normalized) return undefined
  if (normalized === '2' || normalized === 'all_services' || normalized === 'all') return 'all'
  return normalized
}

/** 以后端为准：字符串枚举原样传；仅把旧数字码 1/2 转到常用枚举 */
function resolveTypeFilter(value: string): string {
  if (value === 'all' || !value.trim()) return 'all'
  const normalized = value.trim().toLowerCase()
  if (normalized === '1') return 'fixed_amount'
  if (normalized === '2') return 'percentage_off'
  return normalized
}

export function buildCouponCenterListParams(input: {
  page: number
  pageSize: number
  searchField: string
  searchQuery: string
  type: string
  eligibleService: string
  visibility: string
  availability: string
}): CouponCenterListParams {
  const params: CouponCenterListParams = {
    page: input.page,
    page_size: input.pageSize,
    search_type: resolveSearchType(input.searchField),
    keyword: input.searchQuery.trim(),
    type: resolveTypeFilter(input.type),
    visibility: resolveVisibilityFilter(input.visibility),
    availability: resolveAvailabilityFilter(input.availability),
  }

  const eligibleService = resolveEligibleServiceFilter(input.eligibleService)
  if (eligibleService != null) {
    params.eligible_service = eligibleService
  }

  return params
}

export function mapCouponListItem(item: CouponCenterListItem): CouponItemView {
  const { valueType, value } = splitDiscountText(item.discount_text)
  const detailLike = item as CouponCenterListItem & Partial<CouponDetailData>

  const validUntil = resolveValidityPeriodText({
    coupon_id: item.coupon_id,
    name: item.name ?? '',
    code: item.code ?? '',
    start_mode: detailLike.start_mode,
    start_time: detailLike.start_time,
    end_mode: detailLike.end_mode,
    end_time: detailLike.end_time,
    validity_period_text: item.expiry_text ?? detailLike.validity_period_text,
  })

  const eligibility = resolveEligibleServicesText({
    coupon_id: item.coupon_id,
    name: item.name ?? '',
    code: item.code ?? '',
    eligible_services: detailLike.eligible_services,
    eligible_services_text: item.eligible_text ?? detailLike.eligible_services_text,
  })

  return {
    id: item.coupon_id || String(item.id),
    recordId: item.id,
    name: item.name ?? '',
    code: item.code ?? '',
    validUntil: validUntil || item.expiry_text || '',
    usage: item.usage_text ?? '',
    valueType,
    value,
    spendRequirement: item.min_spend_text ?? '',
    eligibility: eligibility || item.eligible_text || '',
    visibility: item.visibility_text ?? item.visibility ?? '',
    status: item.availability_text ?? item.availability ?? '',
    visibilityRaw: (item.visibility ?? '').toLowerCase(),
    availabilityRaw: (item.availability ?? '').toLowerCase(),
  }
}

export function isCouponActive(coupon: CouponItemView): boolean {
  if (coupon.availabilityRaw) {
    return coupon.availabilityRaw === 'active' || coupon.availabilityRaw === '1'
  }
  return coupon.status.toLowerCase() === 'active'
}

export function isCouponVisible(coupon: CouponItemView): boolean {
  if (coupon.visibilityRaw) {
    return coupon.visibilityRaw === 'visible' || coupon.visibilityRaw === '1'
  }
  return coupon.visibility.toLowerCase() === 'visible'
}

/** POST `/api/admin/coupon/list` */
export async function postCouponCenterList(
  params: CouponCenterListParams,
): Promise<ApiResponse<CouponCenterListData>> {
  if (shouldUseCouponMock()) {
    return getMockCouponListResponse(params) as ApiResponse<CouponCenterListData>
  }
  return request.post<ApiResponse<CouponCenterListData>>('/api/admin/coupon/list', params)
}
